import { Box, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Iconify from '../../../components/Iconify';
import MyAvatar from '../../../components/MyAvatar';
import ChartBlock from './blocks/ChartBlock';
import TableBlock from './blocks/TableBlock';
import ServiceRequestCards from './blocks/ServiceRequestCards';
import MemberCards from './blocks/MemberCards';
import InvoiceCards from './blocks/InvoiceCards';
import CompanyCards from './blocks/CompanyCards';
import WriteConfirmBlock from './blocks/WriteConfirmBlock';
import SuccessBlock from './blocks/SuccessBlock';
import GuideOfferBlock from './blocks/GuideOfferBlock';

function UIBlock({ block, conversationId, onConfirm }) {
  switch (block.type) {
    case 'chart': return <ChartBlock block={block} />;
    case 'table': return <TableBlock block={block} />;
    case 'service_request_card_list': return <ServiceRequestCards block={block} />;
    case 'member_card_list': return <MemberCards block={block} />;
    case 'invoice_card_list': return <InvoiceCards block={block} />;
    case 'company_card_list': return <CompanyCards block={block} />;
    case 'write_confirm': return <WriteConfirmBlock block={block} conversationId={conversationId} onConfirm={onConfirm} />;
    case 'success': return <SuccessBlock block={block} />;
    case 'guide_offer': return <GuideOfferBlock block={block} />;
    default: return null;
  }
}

// Markdown styles injected via sx on the container
const mdStyles = (isUser, theme) => ({
  '& p': { margin: 0, lineHeight: 1.65 },
  '& p + p': { marginTop: '0.5em' },
  '& strong': { fontWeight: 700 },
  '& em': { fontStyle: 'italic', opacity: 0.85 },
  '& code': {
    backgroundColor: isUser ? 'rgba(255,255,255,0.15)' : theme.palette.action.hover,
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: '0.82em',
    fontFamily: '"Fira Code", "JetBrains Mono", monospace',
    color: isUser ? 'inherit' : theme.palette.primary.main,
  },
  '& pre': {
    backgroundColor: isUser ? 'rgba(0,0,0,0.15)' : (theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.04)'),
    borderRadius: 8,
    padding: '12px 16px',
    margin: '8px 0',
    overflowX: 'auto',
    '& code': { backgroundColor: 'transparent', padding: 0, fontSize: '0.82em' },
  },
  '& ul, & ol': { margin: 0, paddingLeft: '1.4em' },
  '& li': { margin: 0, lineHeight: 1.65 },
  '& li + li': { marginTop: '0.2em' },
  '& li p': { margin: 0, display: 'inline' },
  '& a': { color: isUser ? 'rgba(255,255,255,0.9)' : theme.palette.primary.main, textDecoration: 'underline' },
  '& blockquote': {
    borderLeft: `3px solid ${isUser ? 'rgba(255,255,255,0.4)' : theme.palette.primary.main}`,
    margin: '8px 0',
    paddingLeft: 12,
    opacity: 0.85,
  },
  '& h1, & h2, & h3, & h4': { margin: '12px 0 4px', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.01em' },
  '& h1': { fontSize: '1.2em' },
  '& h2': { fontSize: '1.1em' },
  '& h3': { fontSize: '1em' },
  // Tables via remark-gfm
  '& table': { borderCollapse: 'collapse', width: '100%', margin: '10px 0', fontSize: '0.84em' },
  '& th': {
    border: `1px solid ${isUser ? 'rgba(255,255,255,0.2)' : theme.palette.divider}`,
    padding: '6px 12px',
    backgroundColor: isUser ? 'rgba(255,255,255,0.12)' : theme.palette.action.hover,
    fontWeight: 600,
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
  '& td': {
    border: `1px solid ${isUser ? 'rgba(255,255,255,0.15)' : theme.palette.divider}`,
    padding: '6px 12px',
    verticalAlign: 'top',
  },
  '& tr:nth-of-type(even) td': {
    backgroundColor: isUser ? 'rgba(255,255,255,0.04)' : theme.palette.action.selected,
  },
  '& hr': { border: 'none', borderTop: `1px solid ${isUser ? 'rgba(255,255,255,0.2)' : theme.palette.divider}`, margin: '12px 0' },
});

export default function ChatMessage({ message, conversationId, onConfirm }) {
  const isUser = message.role === 'user';
  const content = message.content || '';
  const uiBlocks = message.ui_blocks || [];
  const timestamp = message.created_at || message.timestamp;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 2.5,
        animation: 'msg-in 0.28s cubic-bezier(0.34,1.56,0.64,1)',
        '@keyframes msg-in': {
          from: { opacity: 0, transform: isUser ? 'translateX(12px)' : 'translateX(-12px)' },
          to: { opacity: 1, transform: 'translateX(0)' },
        },
      }}
    >
      <Box sx={{ maxWidth: isUser ? { xs: '86%', sm: '72%' } : { xs: '94%', sm: '90%' }, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>

          {/* Dex avatar — left of AI messages */}
          {!isUser && (
            <Box
              sx={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
              }}
            >
              <Iconify icon="mdi:robot-happy-outline" sx={{ color: 'white', fontSize: 18 }} />
            </Box>
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {content && (
              isUser ? (
                /* User bubble: gradient glass */
                <Box
                  sx={{
                    px: 2, py: 1.5,
                    borderRadius: '16px 16px 4px 16px',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    color: 'white',
                    boxShadow: '0 4px 20px rgba(99,102,241,0.3)',
                    wordWrap: 'break-word',
                  }}
                >
                  <Typography variant="body2" component="div" sx={(theme) => mdStyles(true, theme)}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                  </Typography>
                </Box>
              ) : (
                /* AI bubble: glass card */
                <Box
                  sx={{
                    px: 2, py: 1.5,
                    borderRadius: '4px 16px 16px 16px',
                    backdropFilter: 'blur(12px)',
                    bgcolor: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.05)'
                      : 'rgba(255,255,255,0.85)',
                    border: '1px solid',
                    borderColor: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.08)'
                      : 'rgba(99,102,241,0.12)',
                    boxShadow: (theme) => theme.palette.mode === 'dark'
                      ? '0 4px 24px rgba(0,0,0,0.2)'
                      : '0 4px 24px rgba(0,0,0,0.06)',
                    wordWrap: 'break-word',
                  }}
                >
                  <Typography variant="body2" component="div" sx={(theme) => mdStyles(false, theme)}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                  </Typography>
                </Box>
              )
            )}

            {/* Rich UI blocks */}
            {uiBlocks.map((block, i) => (
              <UIBlock key={i} block={block} conversationId={conversationId} onConfirm={onConfirm} />
            ))}

            {timestamp && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.disabled', mt: 0.5, display: 'block',
                  textAlign: isUser ? 'right' : 'left',
                  fontSize: '0.65rem', letterSpacing: '0.02em',
                }}
              >
                {new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            )}
          </Box>

          {/* User avatar — right of user messages */}
          {isUser && (
            <Box sx={{ flexShrink: 0 }}>
              <MyAvatar sx={{ width: 32, height: 32, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
